<template>
  <v-container
    class="fill-height section-container"
    fluid
    style="display: flex; justify-content: center"
  >
    <v-col cols="12">
      <BillAlert show-close-button />
    </v-col>

    <v-col cols="12" class="pt-6 pt-md-5 pb-md-4">
      <v-row dense>
        <v-col cols="12" md="8">
          <v-text-field
            autofocus
            outlined
            dense
            clearable
            hide-details
            :value="pagination.search"
            @input="debounceSearch($event)"
            @click:clear="clearSearch()"
            flat
            prepend-inner-icon="mdi-magnify"
            label="Buscar"
            color="primary"
          />
        </v-col>

        <v-col>
          <v-select
            dense
            label="Estado"
            outlined
            :value="pagination.status"
            @input="debounceStatus($event)"
            :items="statusOptions"
            item-text="name"
            item-value="value"
            hide-details
            clearable
            flat
            multiple
          />
        </v-col>
      </v-row>
    </v-col>

    <section v-if="requestLoading" class="loader-container">
      <Loader text="Cargando pedidos" />
    </section>

    <template v-else-if="!requestLoading && requests?.length > 0">
      <v-col
        v-if="$store.state.headers"
        cols="12"
        class="hidden-sm-and-down pt-0"
      >
        <v-data-table
          :headers="computedHeaders"
          :items="requests"
          :loading="requestLoading"
          :page.sync="pagination.page"
          :items-per-page.sync="pagination.itemsPerPage"
          :server-items-length="totalRequests"
          :footer-props="{
            itemsPerPageOptions: pagination.rowsPerPageOption,
            itemsPerPageText: 'Pedidos por página',
          }"
          style="width: 100%"
          loading-text="Cargando listado..."
          no-data-text="No se encontraron pedidos"
          hide-default-header
        >
          <template v-slot:header>
            <tr>
              <th v-show="showColumn('request.display_id')">ID</th>
              <th v-show="showColumn('request.status')">ESTADO</th>
              <th v-show="showColumn('request.rubro')">RUBRO</th>
              <th v-show="showColumn('request.service')">SERVICIO</th>
              <th v-show="showClientColumn('request.client')">CLIENTE</th>
              <th v-show="showColumn('request.address')">DIRECCION</th>
              <th v-show="showColumn('request.city')">CIUDAD</th>
            </tr>
          </template>
          <template v-slot:item="{ item: request }">
            <tr v-if="request.is_urgency" class="urgent header">
              <td :colspan="7" style="height: 30px">
                <b style="color: #ff5252"
                  ><span style="font-size: 12px">&#x21E9;</span> PEDIDO URGENTE
                  <span style="font-size: 12px">&#x21E9;</span></b
                >
              </td>
            </tr>
            <tr
              @click="showRequest(request)"
              style="cursor: pointer"
              v-bind:class="{ urgent: request.is_urgency }"
            >
              <td v-show="showColumn('request.display_id')">
                <div style="margin: 0" class="id-column-event">
                  <span>{{ request.display_id ?? request.id }}</span>
                  <v-tooltip bottom>
                    <template
                      v-slot:activator="{ on, attrs }"
                      v-if="$store.state.app_display_scheme && request.scheme"
                    >
                      <div v-bind="attrs" v-on="on">
                        <SchemeIcon
                          :scheme="request.scheme"
                          class="icon-size"
                        />
                      </div>
                    </template>
                    <span>{{ request.scheme }}</span>
                  </v-tooltip>
                </div>
              </td>
              <td v-show="showColumn('request.status')">
                {{ request.status ?? "No disponible" }}
              </td>
              <td v-show="showColumn('request.rubro')">
                {{ request.rubro ?? "No disponible" }}
              </td>
              <td v-show="showColumn('request.service')">
                {{ request.service ?? "No disponible" }}
              </td>
              <td v-show="showClientColumn('request.client')">
                {{ request.client ?? "No disponible" }}
              </td>
              <td v-show="showColumn('request.address')">
                {{ request.address ?? "No disponible" }}
              </td>
              <td v-show="showColumn('request.city')">
                {{ request.city ?? "No disponible" }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>

      <!-- Mobile -->
      <v-col cols="12" class="hidden-md-and-up">
        <v-data-table
          :items="requests"
          :loading="requestLoading"
          :headers="computedHeaders"
          :page.sync="pagination.page"
          :items-per-page.sync="pagination.itemsPerPage"
          :server-items-length="totalRequests"
          :footer-props="{
            itemsPerPageOptions: pagination.rowsPerPageOption,
            itemsPerPageText: 'Pedidos por página',
          }"
          style="width: 100%"
          hide-default-header
          loading-text="Cargando listado..."
          no-data-text="No se encontraron pedidos"
        >
          <template v-slot:item="{ item }">
            <RequestItem :request="item" @show-request="showRequest(item)" />
          </template>
        </v-data-table>
      </v-col>
    </template>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import debounce from "lodash/debounce";

import RequestItem from "./RequestItem.vue";
import HeadersRequests from "../assets/js/headersRequests";
import BillAlert from "./BillAlert.vue";
import SchemeIcon from "./SchemeIcon.vue";
import Loader from "./Loader.vue";

export default {
  name: "request-list",
  components: {
    RequestItem,
    SchemeIcon,
    BillAlert,
    Loader,
  },
  data() {
    return {
      headers: null,
      headersRequests: HeadersRequests,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        rowsPerPageOption: [5, 10, 20, 30],
        search: null,
        status: null,
      },
    };
  },
  created() {
    this.getAllRequests({ pagination: this.pagination });
  },
  mounted() {
    this.$store.commit("set_app_title", "Pedidos");
    window.scrollTo(0, 0);
    this.setHeaders();
  },
  methods: {
    ...mapActions("request", ["getAllRequests"]),

    listRequests: debounce(async function ({ pagination }) {
      try {
        await this.getAllRequests({ pagination });
      } catch (error) {
        console.log(error);
      }
    }, 500),

    debounceSearch: debounce(function (event) {
      this.pagination.search = event;
    }, 500),

    debounceStatus: debounce(function (event) {
      console.log(event);
      this.pagination.status = event;
    }, 500),

    resetPaginationPage() {
      this.pagination.page = 1;
    },
    clearSearch() {
      this.pagination.search = null;
    },
    setHeaders() {
      const _headers = localStorage.getItem("headersRequests");
      let headers;
      if (_headers) {
        headers = JSON.parse(_headers);
      } else {
        headers = this.headersRequests;
        localStorage.setItem("headersRequests", JSON.stringify(headers));
      }
      // this.injectFilters(headers);
      this.$store.commit("set_headers", headers);
    },
    // injectFilters(headers) {
    //   const filter = (value) => {
    //     if (!this.$store.state.request_status) return true;
    //     return value === this.$store.state.request_status;
    //   };
    //   const index = headers.findIndex(
    //     (item) => value === "request.status_request.id"
    //   );
    //   headers[index].filter = filter;
    //   const indexB = this.headersRequests.findIndex(
    //     (item) => value === "request.status_request.id"
    //   );
    //   this.headersRequests[indexB].filter = filter;
    // },
    showRequest(request) {
      this.$router.push({
        name: "request-detail",
        params: { id: request.id },
      });
    },
    showColumn(value) {
      const item = this.$store.state.headers.find(function (element) {
        return element.value === value;
      });
      if (item) return item.show;
      return null;
    },
    showClientColumn(value) {
      const item = this.$store.state.headers.find(function (element) {
        return element.value === value;
      });
      if (item && this.$store.state.app_display_username) return item.show;
      return null;
    },
  },
  computed: {
    ...mapState("request", ["requests", "totalRequests", "statusOptions"]),
    ...mapGetters("request", ["requestLoading"]),

    computedHeaders: function () {
      let _headers = this.headersRequests;
      if (!this.$store.state.app_display_username) {
        _headers = _headers.filter((value) => value !== "request.client");
      }
      return _headers;
    },
  },
  watch: {
    pagination: {
      handler: function (newVal) {
        if (newVal) {
          this.listRequests({ pagination: newVal });
        }
      },
      deep: true,
    },
    "pagination.search": "resetPaginationPage",
    "pagination.status": "resetPaginationPage",
    "$store.state.headers": function (val, old) {
      localStorage.setItem(
        "headersRequests",
        JSON.stringify(this.$store.state.headers)
      );
    },
    "$store.state.updateHeaders": function (val, old) {
      this.setHeaders();
    },
  },
};
</script>
<style scoped>
.loader-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-size {
  max-width: 25px;
  max-height: 20px;
  margin-top: 3px;
}

@media (max-width: 959px) {
  .gap-row {
    gap: 0rem;
  }
}

@media (min-width: 960px) {
  .gap-row {
    gap: 1rem;
  }
}
</style>
<style>
.id-icon-event {
  width: 25px;
}
.id-column-event {
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row-reverse;
  justify-content: flex-end;
  align-items: center;
}
.urgent {
  background-color: #fffbcb;
}
.header:hover {
  background-color: #fffbcb !important;
}
@media (max-width: 959px) {
  .v-divider {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .hidden-sm {
    display: none;
  }
  .section-container {
    padding: 0;
  }
}
@media (min-width: 960px) {
  .v-divider {
    margin-top: 15px;
    margin-bottom: 15px;
  }
  .hidden-lg {
    display: none;
  }
}
.v-list-item__subtitle,
.v-list-item__title {
  overflow: visible !important;
}
.v-data-footer {
  padding: 0 !important;
}
.v-application--is-ltr .v-data-footer__select {
  margin-right: 0 !important;
}
</style>
